import {
  useInfiniteQuery,
  type QueryFunctionContext,
} from "@tanstack/react-query";
import type {
  NormalizedResponse,
  SubcategoryConfig,
} from "../types/source.types";
import { normalizeResponse } from "../utils/normalize";
import { getPublicationsRaw } from "./source.api";

type QKey = [
  "publications",
  {
    subcatId: string;
    endpoint: string;
    responseKey?: string;
    search?: string;
    perPage?: number;
  },
];

async function fetchPage(
  ctx: QueryFunctionContext<QKey, number>,
): Promise<NormalizedResponse> {
  const [{ subcatId, endpoint, responseKey, search = "" }] = ctx.queryKey.slice(
    1,
  ) as any;
  const page = ctx.pageParam ?? 1;

  const raw = await getPublicationsRaw(endpoint, {
    page,
    limit: 10,
    search: search || "",
  });

  // Extract category from endpoint with special mapping for API prefixes
  const endpointPrefix = endpoint.split("/")[0]; // e.g., "print/broadsheet" -> "print"

  // Map API endpoint prefixes to UI categories
  const categoryMapping: Record<string, string> = {
    audio: "radio", // audio/radio -> radio
    video: "tv", // video/tv -> tv
    online: "online", // online/blogs -> online
    print: "print", // print/broadsheet -> print
    "online-news": "online", // online-news -> online (no prefix)
  };

  const category = categoryMapping[endpointPrefix] || endpointPrefix;

  return normalizeResponse(raw, responseKey, category, subcatId);
}

export function usePublicationsInfinite(
  subcat: SubcategoryConfig,
  search?: string,
) {
  return useInfiniteQuery({
    queryKey: [
      "publications",
      {
        subcatId: subcat.id,
        endpoint: subcat.endpoint,
        responseKey: subcat.responseKey,
        search: search || "",
        perPage: subcat.perPage,
      },
    ] satisfies QKey,
    queryFn: fetchPage,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNext ? lastPage.meta.currentPage + 1 : undefined,
  });
}
